import RenderEntity, { RENDER_ENTITY_FORMAT3 } from 'ui-component/renders/RenderEntity';
import PropTypes from 'prop-types';
import { lazy, useCallback, useMemo, useState } from 'react';
import { batch } from 'react-redux';

// material-ui
import { Box, Tooltip, Typography, ButtonBase, Menu } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';

// project imports
import { useDispatch, useSelector } from 'store';
import { appGridActions, appState } from 'store/behaviors/loader';
import capabilities from './capabilities';
import { TASK_TREE_MAX_DEPTH } from './tree';
import { ApiTasksApplyTransItem } from './api';
import { processEntityInput, processEntityOutput } from './serializers';
import { openSnackbar } from 'store/slices/snackbar';
import { openConfirmDialog } from 'store/slices/confirm-dialog';
import RenderWorkflowState, {
    RENDER_WORKFLOW_STATE_FORMAT2,
    RENDER_WORKFLOW_STATE_FORMAT6
} from 'ui-component/renders/Workflow/RenderWorkflowState';
import RenderWorkflowTransition, { RENDER_WORKFLOW_TRANSITION_FORMAT1 } from 'ui-component/renders/Workflow/RenderWorkflowTransition';
import { canCreateWorkOrderFromTask } from 'views/workmgmt/Components/create-navigation';

// assets
import DensityMediumTwoToneIcon from '@mui/icons-material/DensityMediumTwoTone';
import FlagTwoToneIcon from '@mui/icons-material/FlagTwoTone';
import LowPriorityTwoToneIcon from '@mui/icons-material/LowPriorityTwoTone';
import PriorityHighTwoToneIcon from '@mui/icons-material/PriorityHighTwoTone';
import ReceiptTwoToneIcon from '@mui/icons-material/ReceiptTwoTone';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import AddBusinessTwoToneIcon from '@mui/icons-material/AddBusinessTwoTone';

// lazy loaded components
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

const EmployeeSelect = lazy(() => import('ui-component/fields/EntitySelect/common/EmployeeSelect'));
const PrioritySelect = lazy(() => import('ui-component/fields/PrioritySelect'));
const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));

const getTaskMenuTransitions = (task, taskTransitions = [], permissionData = []) =>
    (Array.isArray(taskTransitions) ? taskTransitions : []).filter((trans) => {
        if (!trans?.from?.includes(task?.state)) {
            return false;
        }
        if (!task?.metadata?.transitions?.some((tmd) => tmd?.transition === trans?.name && tmd?.canBeExecutedManually === true)) {
            return false;
        }
        if (
            task?.metadata?.transitions?.some((tmd) => tmd?.transition === trans?.name && tmd?.regressive === true) &&
            trans?.to !== task?.previousState
        ) {
            return false;
        }

        return permissionData?.some((perm) => perm?.endsWith(`canApplyTransition-${trans?.name}`));
    });

const TaskWorkflowStateGridCell = ({ row, permissionData = [], onClickActions = {} }) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const { transitions: taskTransitions } = useSelector((state) => state.task);

    const [transMenuAnchorElem, setTransMenuAnchorElem] = useState(null);
    const [isApplyingTransition, setIsApplyingTransition] = useState(false);

    const menuTransitions = useMemo(
        () => getTaskMenuTransitions(row, taskTransitions, permissionData),
        [row, taskTransitions, permissionData]
    );
    const canApplyTransition = useMemo(
        () =>
            !row?.deletedAt &&
            Boolean(row?.id) &&
            menuTransitions.length > 0 &&
            permissionData?.some((auth) => auth?.startsWith('canApplyTransition')),
        [menuTransitions.length, permissionData, row?.deletedAt, row?.id]
    );

    const handleApplyTransition = useCallback(
        (transitionName) => {
            setTransMenuAnchorElem(null);
            dispatch(
                openConfirmDialog({
                    title: <FormattedMessage id="ui-comp-confirmdialog-exectransition-title" />,
                    dialogMsg: <FormattedMessage id="ui-comp-confirmdialog-exectransition-content" />,
                    closeOnProceed: true,
                    handleProceedAction: () => {
                        setIsApplyingTransition(true);
                        ApiTasksApplyTransItem({
                            id: row?.id,
                            transition: transitionName,
                            data: processEntityOutput(row),
                            updateStore: false,
                            successCallback: (response) =>
                                batch(() => {
                                    const updatedTask = processEntityInput(response);

                                    setIsApplyingTransition(false);
                                    dispatch(
                                        openSnackbar({
                                            open: true,
                                            message: intl.formatMessage({ id: 'ui-comp-snackbar-success-text' }),
                                            variant: 'alert',
                                            alert: { color: 'success' }
                                        })
                                    );

                                    if (typeof onClickActions?.onClickRowActionApplyTransitionSuccess === 'function') {
                                        onClickActions.onClickRowActionApplyTransitionSuccess(row, updatedTask, response);
                                    }
                                }),
                            errorCallback: (error) =>
                                batch(() => {
                                    setIsApplyingTransition(false);
                                    dispatch(
                                        openSnackbar({
                                            open: true,
                                            message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                                            variant: 'alert',
                                            alert: { color: 'error' }
                                        })
                                    );
                                })
                        });
                    }
                })
            );
        },
        [dispatch, intl, onClickActions, row]
    );

    if (!canApplyTransition) {
        return <RenderWorkflowState value={row?.state} renderFormat={RENDER_WORKFLOW_STATE_FORMAT2} />;
    }

    return (
        <>
            <ButtonBase
                sx={{ borderRadius: '12px' }}
                disabled={isApplyingTransition}
                onClick={(event) => {
                    event.stopPropagation();
                    setTransMenuAnchorElem(event.currentTarget);
                }}
            >
                <RenderWorkflowState value={row?.state} renderFormat={RENDER_WORKFLOW_STATE_FORMAT2} />
            </ButtonBase>
            <Menu
                id={`task-transitions-menu-${row?.id || row?.treeNodeId}`}
                anchorEl={transMenuAnchorElem}
                keepMounted
                open={Boolean(transMenuAnchorElem)}
                onClose={() => setTransMenuAnchorElem(null)}
                variant="selectedMenu"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClick={(event) => event.stopPropagation()}
            >
                {menuTransitions.map((trans) => (
                    <RenderWorkflowTransition
                        key={`menuitem-${row?.id || row?.treeNodeId}-${trans?.name}`}
                        value={trans?.name}
                        onClickRowActionTransition={handleApplyTransition}
                        renderFormat={RENDER_WORKFLOW_TRANSITION_FORMAT1}
                    />
                ))}
            </Menu>
        </>
    );
};

TaskWorkflowStateGridCell.propTypes = {
    row: PropTypes.object,
    permissionData: PropTypes.arrayOf(PropTypes.string),
    onClickActions: PropTypes.object
};

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => {
                const gridActions = appGridActions(
                    capabilities,
                    params?.row,
                    permissionData?.filter((perm) => !['canAdd', 'canImport', 'canExport'].includes(perm)),
                    onClickActions
                );

                if (!params?.row?.deletedAt && permissionData?.some((perm) => perm?.startsWith('notes-'))) {
                    gridActions.push({
                        key: `action-task-notes-row${params?.row?.id || params?.row?.treeNodeId}`,
                        icon: <SpeakerNotesIcon fontSize="small" />,
                        label: 'entity-workmgmt-note-plural',
                        position: 20,
                        showInMenu: true,
                        onClick: () => {
                            if (
                                'onClickRowActionOpenNotes' in (onClickActions || {}) &&
                                typeof onClickActions.onClickRowActionOpenNotes === 'function'
                            ) {
                                onClickActions.onClickRowActionOpenNotes(params?.row);
                            }
                        }
                    });
                }

                if (!params?.row?.deletedAt && permissionData?.some((perm) => perm?.startsWith('tickets-'))) {
                    gridActions.push({
                        key: `action-task-tickets-row${params?.row?.id || params?.row?.treeNodeId}`,
                        icon: <ReceiptTwoToneIcon fontSize="small" />,
                        label: 'entity-workmgmt-ticket-plural',
                        position: 21,
                        showInMenu: true,
                        onClick: () => {
                            if (
                                'onClickRowActionOpenTickets' in (onClickActions || {}) &&
                                typeof onClickActions.onClickRowActionOpenTickets === 'function'
                            ) {
                                onClickActions.onClickRowActionOpenTickets(params?.row);
                            }
                        }
                    });
                }

                if (
                    !params?.row?.deletedAt &&
                    permissionData?.some((perm) => perm?.startsWith('followups-')) &&
                    (params?.row?.followUp || params?.row?.requiresFollowUp)
                ) {
                    gridActions.push({
                        key: `action-task-followup-row${params?.row?.id || params?.row?.treeNodeId}`,
                        icon: <FlagTwoToneIcon fontSize="small" />,
                        label: 'entity-workmgmt-followup',
                        position: 22,
                        showInMenu: true,
                        onClick: () => {
                            if (
                                'onClickRowActionOpenFollowUp' in (onClickActions || {}) &&
                                typeof onClickActions.onClickRowActionOpenFollowUp === 'function'
                            ) {
                                onClickActions.onClickRowActionOpenFollowUp(params?.row);
                            }
                        }
                    });
                }

                if (!params?.row?.deletedAt && permissionData?.includes('canAdd') && params?.row?.treeDepth < TASK_TREE_MAX_DEPTH - 1) {
                    gridActions.push({
                        key: `action-task-add-subtask-row${params?.row?.id || params?.row?.treeNodeId}`,
                        icon: <AddTwoToneIcon fontSize="small" />,
                        label: 'ui-comp-button-add',
                        position: 5,
                        showInMenu: true,
                        onClick: () => {
                            if (
                                'onClickRowActionAddSubTask' in (onClickActions || {}) &&
                                typeof onClickActions.onClickRowActionAddSubTask === 'function'
                            ) {
                                onClickActions.onClickRowActionAddSubTask(params?.row);
                            }
                        }
                    });
                }

                if (!params?.row?.deletedAt && canCreateWorkOrderFromTask(params?.row) && params?.row?.allowsWorkOrderCreation) {
                    gridActions.push({
                        key: `action-task-create-workorder-row${params?.row?.id}`,
                        icon: <AddBusinessTwoToneIcon fontSize="small" />,
                        label: 'ui-comp-button-create-workorder',
                        position: 23,
                        showInMenu: true,
                        onClick: () => {
                            if (
                                'onClickRowActionCreateWorkOrder' in (onClickActions || {}) &&
                                typeof onClickActions.onClickRowActionCreateWorkOrder === 'function'
                            ) {
                                onClickActions.onClickRowActionCreateWorkOrder(params?.row);
                            }
                        }
                    });
                }

                return gridActions;
            }
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'code',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentityhasautogencode-code'
        },
        {
            field: 'name',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentityisdescribable-name'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            flex: 1,
            label: 'traits-appentityisdescribable-description',
            renderCell: (params) =>
                params?.value ? (
                    <Tooltip
                        title={
                            <div
                                style={{ maxWidth: 'auto', maxHeight: 'auto', overflow: 'auto' }}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: params.value.replace(/^((?:.|\n|\r){400})(?:.|\n|\r){2,}/, '$1...') }}
                            />
                        }
                    >
                        <div
                            style={{ maxWidth: 'auto', maxHeight: 'auto', overflow: 'auto' }}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: params.value.replace(/^((?:.|\n|\r){400})(?:.|\n|\r){2,}/, '$1...') }}
                        />
                    </Tooltip>
                ) : null
        },
        {
            field: 'priority',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) =>
                params?.value ? (
                    <Tooltip title={<FormattedMessage id={`ui-comp-form-priority-${params.value || 'low'}`} />}>
                        <Box>
                            {params.value === 'low' && <LowPriorityTwoToneIcon fontSize="small" />}
                            {params.value === 'medium' && <DensityMediumTwoToneIcon fontSize="small" />}
                            {params.value === 'high' && <PriorityHighTwoToneIcon fontSize="small" />}
                        </Box>
                    </Tooltip>
                ) : null,
            label: 'entity-workmgmt-task-priority'
        },
        {
            field: 'responsible',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="employee"
                    renderPattern="<name> <lastName>"
                    renderTooltipPattern="[<code>] <name> <lastName>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-workmgmt-task-responsible'
        },
        {
            field: 'state',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params, permissionData, onClickActions) => (
                <TaskWorkflowStateGridCell row={params?.row} permissionData={permissionData} onClickActions={onClickActions} />
            ),
            label: 'entity-workmgmt-task-state'
        },
        {
            field: 'previousState',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) => <RenderWorkflowState renderFormat={RENDER_WORKFLOW_STATE_FORMAT2} {...params} />,
            label: 'entity-workmgmt-task-previousstate'
        },
        {
            field: 'followUp',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            renderCell: (params) =>
                Array.isArray(params?.value?.steps) ? (
                    <RenderWorkflowState value={params.value.steps} renderFormat={RENDER_WORKFLOW_STATE_FORMAT6} />
                ) : null,
            label: 'entity-workmgmt-followup'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            valueGetter: (params) => params?.value && parseJSON(params?.value),
            renderCell: (params) =>
                params?.value ? (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ) : null,
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        },
        {
            field: 'deletedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            field: 'deletedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityissafedeletable-deletedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                code: true,
                name: false,
                description: false,
                priority: true,
                responsible: true,
                state: true,
                previousState: false,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false,
                deletedAt: false,
                deletedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'code',
                'name',
                'description',
                'priority',
                'responsible',
                'state',
                'previousState',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy',
                'deletedAt',
                'deletedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                code: { width: 100 },
                name: { width: 200 },
                description: { width: 100 },
                priority: { width: 100 },
                responsible: { width: 100 },
                state: { width: 100 },
                previousState: { width: 100 },
                createdAt: { width: 100 },
                createdBy: { width: 100 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 },
                deletedAt: { width: 100 },
                deletedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }]
        },
        toolbarFilters: { loadUserTasks: false, includeSafeDeleted: false }
    },
    filterDef: [
        {
            filterType: 'string',
            field: { fieldName: 'code', fieldType: 'simple' },
            label: 'traits-appentityhasautogencode-code'
        },
        {
            filterType: 'string',
            field: { fieldName: 'name', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-name'
        },
        {
            filterType: 'string',
            field: { fieldName: 'description', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-description'
        },
        {
            filterType: 'array',
            field: { fieldName: 'priority', fieldType: 'simple' },
            label: 'entity-workmgmt-task-priority',
            renderField: (params) => <PrioritySelect key="filterTask-priority" multiple {...params} />
        },
        {
            filterType: 'reference',
            field: { fieldName: 'responsible', fieldType: 'joined' },
            label: 'entity-workmgmt-workorder-responsible',
            renderField: (params) => <EmployeeSelect key="filterTask-responsible" multiple {...params} />
        },
        {
            filterType: 'string',
            field: { fieldName: 'state', fieldType: 'simple' },
            label: 'entity-workmgmt-task-state'
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => <UserSelect key="filterTask-createdBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => <UserSelect key="filterTask-updatedBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'deletedAt', fieldType: 'simple' },
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'deletedBy', fieldType: 'simple', outputFieldName: 'deletedById' },
            label: 'traits-appentityissafedeletable-deletedby',
            renderField: (params) => <UserSelect key="filterTask-deletedBy" multiple displayMode="fullName" {...params} />
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder',
        fields: [
            { fieldName: 'code', fieldType: 'simple' },
            { fieldName: 'name', fieldType: 'simple' },
            { fieldName: 'description', fieldType: 'simple' },
            { fieldName: 'parentCode', fieldType: 'simple' },
            { fieldName: 'parentName', fieldType: 'simple' },
            { fieldName: 'parentClientName', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'code', fieldType: 'simple' },
        { fieldName: 'name', fieldType: 'simple' },
        { fieldName: 'description', fieldType: 'simple' },
        { fieldName: 'priority', fieldType: 'simple' },
        { fieldName: 'responsible', fieldType: 'joined' },
        { fieldName: 'state', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' }
    ]
};

export default initialState;
